import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import { ISocialMediaStatus } from "../../../store/repIQSocialMediaStatus/types";
import PresetProfile from "./../PresetProfile";
import ResultChart from "./../ResultChart";
import SocialMediaDropDown from "./../SocialMedia/SocialMediaDropDown";
import ScoreAverages from "./ScoreAverages";
import ScoreHistory from "./ScoreHistory";

interface IProps {
    title: string;
    name: string;
    scoreDescription: any;
}

const ReportDetails: React.FunctionComponent<IProps> = (props) => {
    const SelectedNetwork: ISocialMediaStatus = useSelector(
        (state: ApplicationState) => state.repIQSelectedSocialMediaState && state.repIQSelectedSocialMediaState.selectedSocialMedia
    );
    const [selectedPresetProfile, setSelectedPresetProfile] = useState({} as any);
    const [score, setScore] = useState(0);
    const [avgScore, setAvgScore] = useState(0);
    const [rangeHigh, setRangeHigh] = useState(0);
    const [rangeLow, setRangeLow] = useState(0);
    const [scoreDescription, setScoreDescription] = useState("");
    const [tooltip, setTooltip] = useState("");
    const [benchmarkText, setBenchmarkText] = useState("");
    const [scoreDescriptionList, setScoreDescriptionList] = useState([] as any);

    const updateSelectedPresetProfile = (profile: any) => {
        setSelectedPresetProfile(profile);
    };

    const updateSelectedNetwork = (network: any) => {
        let sc = Math.round(network[props.name] * 100);
        setScore(sc);
        calculateScoreDescription(sc);
    };

    const calculateScoreDescription = (score: number) => {
        if (score >= 0 && score <= 35) {
            setScoreDescription("Low");
            setScoreDescriptionList(props.scoreDescription["Low"]);
        } else if (score >= 36 && score <= 64) {
            setScoreDescription("Moderate");
            setScoreDescriptionList(props.scoreDescription["Moderate"]);
        } else if (score >= 65 && score <= 100) {
            setScoreDescription("High");
            setScoreDescriptionList(props.scoreDescription["High"]);
        }
    };

    useEffect(() => {
        if (selectedPresetProfile && selectedPresetProfile?.categories) {
            let high: number = Math.round(selectedPresetProfile.categories[props.name].high);
            let low: number = Math.round(selectedPresetProfile.categories[props.name].low);

            setAvgScore((high + low) / 2);
            setRangeHigh(high);
            setRangeLow(low);

            if (low <= score && score <= high) {
                setBenchmarkText("Within Preset Profile Benchmark");
            } else if (low === 0 && high === 0) {
                setBenchmarkText("");
            } else {
                setBenchmarkText("Outside Preset Profile Benchmark");
            }
        }
    }, [selectedPresetProfile]);

    useEffect(() => {
        if (props.name === "OpennessToExperiences") {
            setTooltip("Openness to Experiences describes your tendency to try new things and think in abstract ways.");
        } else if (props.name === "Conscientiousness") {
            setTooltip("Conscientiousness describes your tendency to focus on achieving your goals.");
        } else if (props.name === "Extraversion") {
            setTooltip("Extraversion describes your tendency to be energized by social interactions.");
        } else if (props.name === "Agreeableness") {
            setTooltip("Agreeableness describes your tendency to put the needs of others ahead of your own needs.");
        } else if (props.name === "EmotionalRange") {
            setTooltip("Emotional Range describes your susceptibility to stress and negative emotions.");
        }
    }, []);

    useEffect(() => {
        if (SelectedNetwork) updateSelectedNetwork(SelectedNetwork);
    }, [SelectedNetwork]);

    return (
        <React.Fragment>
            <div className="box ">
                <div className="box-content">
                    <SocialMediaDropDown disableAction={false} />
                    {SelectedNetwork.MinimumWordCountFoundForAIProcess === true && SelectedNetwork.IsPersonalityAnalysing === false ? (
                        <React.Fragment>
                            <PresetProfile updateSelectedPresetProfile={updateSelectedPresetProfile} isSmallVersion={false} resetProfile={0} />

                            <h3 className="content-sub-title">Your {props.title} Score Summary</h3>
                            <div className="margin-bottom-m"></div>
                            <ul className="report-grid-layout">
                                <li>
                                    <div className="big-number-container">
                                        <div className="value-score-container">
                                            <div className="value-score">{score}</div>
                                            <div className="value-score-text">
                                                {benchmarkText}
                                                <small>({score} of 100)</small>
                                            </div>
                                        </div>
                                        <div className="value-rating-container">
                                            <div className="value-rating">{scoreDescription}</div>
                                            <div className="value-rating-text">{props.title}</div>
                                        </div>
                                    </div>
                                </li>
                                <li>
                                    <ResultChart
                                        title={props.title}
                                        name={props.name}
                                        data={score}
                                        profileName={selectedPresetProfile.profileName}
                                        rangeHigh={rangeHigh}
                                        rangeLow={rangeLow}
                                        tooltip={tooltip}
                                        hasCTAButton={false}
                                    />
                                </li>
                                <li>
                                    <div className="container-border container-attributes" style={{ minHeight: "216px" }}>
                                        <h4 className="content-title">
                                            People {scoreDescription} in {props.title} are seen as:
                                        </h4>
                                        <ul className="attribute-cloud">
                                            {scoreDescriptionList.map((sd: any, index: number) => (
                                                <li key={index}>{sd}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </li>
                                <li>
                                    <div className="container-border">
                                        <ScoreAverages title={props.title} score={score} average={avgScore} />
                                    </div>
                                </li>
                            </ul>

                            <div className="margin-bottom-xl"></div>
                            <div className="line-divider margin-bottom-l"></div>

                            <ScoreHistory
                                title={props.title}
                                name={props.name}
                                selectedNetworkID={SelectedNetwork.NetworkID}
                                presetProfileAverage={avgScore}
                                presetProfileBenchmarkHigh={Math.round(rangeHigh)}
                                presetProfileBenchmarkLow={Math.round(rangeLow)}
                            />
                        </React.Fragment>
                    ) : SelectedNetwork.IsPersonalityAnalysing === true ? (
                        <div className="box">
                            <div className="box-content text-align-center" style={{ minHeight: "200px", backgroundColor: "#F2F6F9" }}>
                                <div style={{ minHeight: "90px" }} />
                                <div className="box-content text-align-center">
                                    <p>Allow a few minutes for RepIQ to create your profile.</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="box">
                            <div className="box-content text-align-center" style={{ minHeight: "200px", backgroundColor: "#F2F6F9" }}>
                                <div style={{ minHeight: "90px" }} />
                                <div className="box-content text-align-center">
                                    <p>
                                        RepIQ requires posted content from a social media account to contain at least 100 words.
                                        <br />
                                        For more accurate analysis, connect a social media account with posts containing at least 600 words.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="margin-bottom-xl"></div>
                </div>
            </div>
        </React.Fragment>
    );
};
export default ReportDetails;
